import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Col,
  Collapse,
  ControlLabel,
  FormControl,
  FormGroup,
  Modal, OverlayTrigger,
  Popover,
  Row,
} from 'react-bootstrap';
import classNames from 'classnames';
import { Context } from '..';
import {
  ADD_LEVEL_SUCCESS,
  CHANGE_VIEW,
  DELETE_LEVEL,
  EDIT_LEVEL_SUCCESS,
  LIST,
} from '../reducer/actionTypes';
import { ADD, EDIT } from '../constants/views';
import { delay, getErrorMessage } from '../../../../../../../utils/helper';
import AlertComponent from '../../../../../../../components/Alert';
import Loader from '../../../../../../../components/Loaders/Loader';
import {
  getCountsValue,
  validateEditLevel,
  validateNewLevel,
} from '../../helpers';
import Tooltip from '../../../../../../../components/Tooltip';
import Translations from '../../../../../../../components/Collapse/Translations';

const ViewEdit = () => {
  let ref = useRef(null);

  const [state, contextDispatch] = useContext(Context)
  const { languages, levels, edit_level_index, onHide, view } = state;
  const [openTranslations, setOpenTranslations] = useState(false);
  const [data, setData] = useState({
    isLoading: false,
    error: null,
    isDefault: false,
    form: {
      id: null,
      name: {},
      discount: 0,
      total_spent: 0,
    },
  })
  const { isLoading, error, form, isDefault } = data;
  const defaultLanguage = useMemo(() => languages.find((language) => language.is_default), [languages])
  const otherLanguages = useMemo(() => languages.filter((language) => !language.is_default), [languages])

  const onSubmit = async (e) => {
    e.preventDefault();
    setData((prevState) => ({
      ...prevState,
      isLoading: true,
    }))
    try {
      await delay(500);
      if (view === EDIT) {
        validateEditLevel(defaultLanguage, levels, edit_level_index, form)
        contextDispatch({ type: EDIT_LEVEL_SUCCESS, payload: form })
      }
      if (view === ADD) {
        validateNewLevel(defaultLanguage, levels, form)
        contextDispatch({ type: ADD_LEVEL_SUCCESS, payload: form })
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error)
      setData((prevState) => ({
        ...prevState,
        isLoading: false,
        error: errorMessage,
      }))
    }
  }

  const onChangeDiscount = (e) => {
    const value = e.target.value;
    setData((prevState) => ({
      ...prevState,
      form: {
        ...prevState.form,
        discount: getCountsValue(value, 100),
      },
    }))
  }

  const onChangeTotalSpent = (e) => {
    const value = e.target.value;

    setData((prevState) => ({
      ...prevState,
      form: {
        ...prevState.form,
        total_spent: getCountsValue(value),
      },
    }))
  }

  const onBack = () => {
    contextDispatch({
      type: CHANGE_VIEW,
      payload: {
        view: LIST,
      },
    })
  }

  const onDelete = () => {
    contextDispatch({ type: DELETE_LEVEL })
  }

  const onChangeName = (value, code) => {
    setData((prevState) => ({
      ...prevState,
      form: {
        ...prevState.form,
        name: {
          ...prevState.form.name,
          [code]: value,
        },
      },
    }))
  }

  useEffect(() => {
    if (view === EDIT) {
      const level = levels[edit_level_index];
      if (level) {
        setData((prevState) => ({
          ...prevState,
          isDefault: level.is_default,
          form: {
            ...prevState.form,
            id: level.id,
            name: level.name,
            discount: level.discount,
            total_spent: level.total_spent,
          },
        }))
      }
    } else {
      setData((prevState) => ({
        ...prevState,
        form: {
          ...prevState.form,
          name: languages.reduce((accumulator, currentValue) => {
            accumulator[currentValue.language_code] = '';
            return accumulator;
          }, {}),
        },
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const popover = (
    <Popover id="popover-trigger-click-root-close" title="" className="text-center">
      <Button bsStyle="primary" bsSize="sm" onClick={onDelete}>Delete</Button>
      <Button bsSize="sm" className="ml-8" onClick={() => ref?.handleHide()}>Cancel</Button>
    </Popover>
  );

  return (
    <form onSubmit={onSubmit}>
      <Modal.Header closeButton onHide={onHide}>
        <Modal.Title>{view === EDIT ? 'Edit user level' : 'Add user level'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <AlertComponent text={error} />}
        {isLoading && <Loader /> }
        <FormGroup
          className={classNames({
            'form-group__rtl': defaultLanguage.direction === 'rtl',
          })}
        >
          <ControlLabel htmlFor={`level_name_${defaultLanguage.language_code}`}>
            Name
            {' '}
            <Badge>{defaultLanguage.language_name}</Badge>
          </ControlLabel>
          <Translations onOpen={() => setOpenTranslations(!openTranslations)} open={openTranslations} items={languages}>
            <FormControl
              type="text"
              id={`level_name_${defaultLanguage.language_code}`}
              name={`name-${defaultLanguage.language_code}`}
              value={form.name[defaultLanguage.language_code] || ''}
              onChange={(e) => onChangeName(e.target.value, defaultLanguage.language_code)}
              required
            />
          </Translations>
        </FormGroup>
        {otherLanguages.length ? (
          <Collapse in={openTranslations}>
            <div>
              {otherLanguages.map((language, index) => {
                const { language_name, language_code, direction } = language;
                return (
                  <FormGroup
                    key={index}
                    className={classNames({
                      'form-group__rtl': direction === 'rtl',
                    })}
                  >
                    <ControlLabel htmlFor={`level_name_${language_code}`}>
                      Name
                      {' '}
                      <Badge>{language_name}</Badge>
                    </ControlLabel>
                    <FormControl
                      type="text"
                      id={`level_name_${language_code}`}
                      name={`name-${language_code}`}
                      value={form.name[language_code] || ''}
                      onChange={(e) => onChangeName(e.target.value, language_code)}
                    />
                  </FormGroup>
                );
              })}
            </div>
          </Collapse>
        ) : null}
        <Row className="level-system__counts">
          <Col md={6}>
            <FormGroup>
              <ControlLabel htmlFor="discount">Discount, %</ControlLabel>
              <FormControl value={form.discount} type="text" id="discount" name="discount" onChange={onChangeDiscount} required />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <ControlLabel htmlFor="total_spent">
                Total user spent from
                {' '}
                <Tooltip
                  placement="top"
                  text={(
                    <p>
                      Set the total spent required for
                      <br />
                      {' '}
                      users to unlock this user
                      <br />
                      level on your panel.
                    </p>
                  )}
                  tooltipClass="tooltip__top"
                >
                  <span className="fas fa-info-circle cursor-help" />
                </Tooltip>
              </ControlLabel>
              <FormControl disabled={isDefault} value={form.total_spent} type="text" id="total_spent" name="total_spent" onChange={onChangeTotalSpent} required />
            </FormGroup>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer className="custom__modal-footer modal-footer">
        <Button
          disabled={isLoading}
          type="submit"
          bsStyle="primary"
        >
          <span>{view === EDIT ? 'Save changes' : 'Add level'}</span>
        </Button>
        <Button
          onClick={onBack}
        >
          <span>Cancel</span>
        </Button>
        {view === EDIT && !isDefault && (
          <div className="pull-right">
            <OverlayTrigger ref={(r) => (ref = r)} trigger="click" rootClose placement="top" overlay={popover}>
              <Button
                disabled={isLoading}
                bsStyle="link"
                className="modal-delete-btn"
              >
                Delete
              </Button>
            </OverlayTrigger>
          </div>
        )}
      </Modal.Footer>
    </form>
  )
}

ViewEdit.propTypes = {};

export default ViewEdit;
